import axios from 'axios';
import { getAccountId } from './account';
import { showErrorGlobal } from './showErrorGlobal';
import {LOCAL_AUTH_STORAGE_KEYS} from '@features/auth/constants';
import {
  clearStoredAccessToken,
  getStoredAccessToken,
  getStoredRefreshToken,
  setStoredAccessToken,
  setStoredRefreshToken,
} from '@features/auth/utils/storage';

export const LOCAL_STORAGE_KEY = 'customApiBase';
export const TEAMS_BASE_URLS = [
  'https://mira-teams.matteroom.com',
  'https://ap-mira-teams.matteroom.com',
];

export const WEB_BASE_URLS = [
  'https://app.miranow.ai',
  'https://ap-app.miranow.ai',
];

const getFallbackBaseURLs = () => {
  return  process.env.PLATFORM_ENV === 'web' ? WEB_BASE_URLS : TEAMS_BASE_URLS;
}

export const getBaseURL =()=>{
  return localStorage.getItem(LOCAL_STORAGE_KEY) || "";
}

export const setBaseURL = (url) => {
  if (url) {
    localStorage.setItem(LOCAL_STORAGE_KEY, url);
  } else {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  }
  window.location.reload();
};

export const avatarURL = (avatar) => {
  const DEFAULT_AVATAR = 'https://attitturesource.blob.core.windows.net/resource/102241652669c05b05d215ec31f54bbd.png';
  
  if (avatar == '/') return DEFAULT_AVATAR;

  const isFullUrl = (url) => /^https?:\/\//.test(url);
  const customBaseURL = getBaseURL();
  return (isFullUrl(avatar)
                  ? avatar
                  : customBaseURL
                      ? buildFullURL(customBaseURL, avatar)
                      : buildFullURL(window.location.origin, avatar));
}

export const buildFullURL = (base, relative) => {
  return base.replace(/\/+$/, '') + '/' + relative.replace(/^https?:\/\/[^/]+/, '').replace(/^\/+/, '');
};

// Shared in-flight refresh-token request so concurrent 401s only trigger one refresh call.
let refreshTokenPromise = null;

const refreshAccessToken = () => {
  if (!refreshTokenPromise) {
    const customBaseURL = getBaseURL();
    const url = customBaseURL ? buildFullURL(customBaseURL, '/api/v1.0/refresh_token/') : '/api/v1.0/refresh_token/';

    refreshTokenPromise = axios.post(url, {
      token: getStoredRefreshToken(),
    }, {
      headers: {
        'Content-Type': 'application/json',
        ...(customBaseURL ? { 'x-api-baseurl': customBaseURL } : {}),
      },
    })
      .then((response) => response.data)
      .finally(() => {
        refreshTokenPromise = null;
      });
  }

  return refreshTokenPromise;
};

// 递归重试请求
const retryRequestWithFallbackURLs = async (originalConfig, originalResponse, triedURLs = []) => {
  const remainingURLs = getFallbackBaseURLs().filter((url) => !triedURLs.includes(url));

  for (const url of remainingURLs) {
    const retryConfig = {
      ...originalConfig,
      _retryWithBaseFallback: true, // 防止无限重试
      url: buildFullURL(url, originalConfig.url),
      headers: {
        ...originalConfig.headers,
        'x-api-baseurl': url,
      },
    };

    const response = await axios.request(retryConfig);
    const code = response?.data?.code;

    if (code !== 404001 && code !== 404003) {
      localStorage.setItem(LOCAL_STORAGE_KEY, url);
      return response;
    } else {
      triedURLs.push(url);
    }
  }

  // 都失败则手动抛出原响应
  showErrorGlobal(originalResponse.data.message || 'Unexpected error occurred');
  return originalResponse;
};


const customInstance = () => {
  const instance = axios.create({
    timeout: 300000,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getStoredAccessToken()}`,
    },
  });

  instance.interceptors.request.use(
    (config) => {
      const customBaseURL = getBaseURL();
      // 拼接完整 URL（仅对非完整 URL 的相对路径生效）
      if (customBaseURL && config.url && !/^https?:\/\//.test(config.url)) {
        config.url = buildFullURL(customBaseURL, config.url);
      }
      if (customBaseURL) {
        config.headers = {
          ...config.headers,
          'x-api-baseurl': customBaseURL,
        };
      }

      const accountId = getAccountId();
      if (accountId && config.url?.includes('/accounts/ACCOUNT_ID/')) {
        config.url = config.url.replace(
          '/accounts/ACCOUNT_ID/',
          `/accounts/${accountId}/`
        );
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

    instance.interceptors.response.use(
    async (response) => {
        const code = response?.data?.code;

        // 拦截 code === 404001 的情况，尝试 fallback
        if ((code === 404001 || code === 404003) && !response.config._retryWithBaseFallback) {
          const currentBaseURL = getBaseURL();
          const fallbackResponse = await retryRequestWithFallbackURLs(response.config, response, currentBaseURL ? [currentBaseURL] : []);
          return fallbackResponse;
        }
        
        if (code && code !== 200000) {
            const message = response.data.message ||  response.data.error || 'Unexpected error occurred';
            showErrorGlobal(message);
        }

        return response;
    },
    async (error) => {

        if (error.response) {
              const { status, data } = error.response;
              const message = data?.message || data?.error || data || 'Unexpected error occurred';

              if (status === 302) {
                  showErrorGlobal('Request redirected unexpectedly (302). Please check your session or permissions.');
                  // 不做 token 清理，不跳转登录页
                  return Promise.reject(error);
              } else if (status === 401) {
                const originalConfig = error.config;

                // Only retry once: refresh the token and retry the original request,
                // and only log out if it is still 401 after that retry.
                if (!originalConfig?._retriedAfterRefresh) {
                  try {
                    const refreshData = await refreshAccessToken();
                    if (refreshData?.refresh_token) {
                      setStoredRefreshToken(refreshData.refresh_token);
                    }
                    if (refreshData?.token) {
                      setStoredAccessToken(refreshData.token);

                      const retryConfig = {
                        ...originalConfig,
                        _retriedAfterRefresh: true,
                        headers: {
                          ...originalConfig.headers,
                          Authorization: `Bearer ${refreshData.token}`,
                        },
                      };

                      return instance.request(retryConfig);
                    }
                  } catch (refreshErr) {
                    // Refresh failed - fall through to logout below.
                  }
                }

                clearStoredAccessToken();
                localStorage.setItem(LOCAL_AUTH_STORAGE_KEYS.showBreak, 'true');
                window.location.reload();
              } else {
                  showErrorGlobal(message);
              }

        } else {
          showErrorGlobal('Network error, please try again');
        }

        return Promise.reject(error);
      }
    );


  return instance;
};

export default customInstance;
